import { isCanceledRequest } from "./isCanceledRequest";
import { isValidRequestId } from "./requestId";

/**
 * Reduces a failed api client call to what a page may keep and show (GROWTH-6580): the HTTP status, whether the
 * request never got an answer, and the request id support can search the logs for. It never carries the server's
 * message or a stack: api-v2 error text is not written for members.
 *
 * The id is the one the client sent as X-Request-Id (api/client.js stores it on the request config, so it is there
 * even when the network failed, and the server adopts a valid one). The response header is only a fallback: production
 * nginx sends no Access-Control-Expose-Headers (docs/prod-nginx-cors-x-request-id.md), so a cross-origin dashboard
 * cannot read it. Anything that is not a UUID v4 is dropped.
 *
 * @param {Object} error axios-style error from api/client.js
 * @param {Object} [extra] fields to keep beside the three below, e.g. { leadId }
 * @returns {{ status: number|null, isNetworkError: boolean, requestId: string|null }} `extra` is spread first, so it cannot overwrite these
 */
export const toLoadError = (error, extra = {}) => {
  const requestId = [error?.config?.metadata?.requestId, error?.response?.headers?.["x-request-id"]].find(isValidRequestId);

  return {
    ...extra,
    status: error?.response?.status ?? null,
    // An axios error with no response: dropped connection, timeout, CORS. A TypeError thrown while reading a
    // body that did arrive is not one, and neither is a cancelled request.
    isNetworkError: Boolean(error?.isAxiosError && !error.response) && !isCanceledRequest(error),
    requestId: requestId ?? null
  };
};
